// ============================================
// clock-double-tap — one person, one open shift
// ============================================
// Side-effect module (UMD): attaches `window.ClockShiftGuard`. app.js imports it
// once, before any view module, and views read it through
// `window.ClockShiftGuard` at CALL time — never a cross-`?v=` ES import
// (Pitfall #1). It imports nothing: every Firestore call goes through the
// `deps` object the caller hands in (its own db / doc / query / ... imports),
// so Node unit tests require() it with fakes:
// `node tests/clock-double-tap.unit.cjs`.
//
// WHY. Two taps at clock-in (two tabs, a phone and the yard kiosk, or a tap
// that lands while the first is still saving) wrote two OPEN shifts for one
// person — reproduced 2026-10-02 (tests/clock-double-tap.spec.js 1a/1b). The
// office then has to guess which one is real, and the newest is usually the
// accidental one, so "close the newest" can close the real shift.
//
// THE GUARD, in the order a clock-in runs it (guardClockIn):
//   1. begin(personId) — same tab: a second tap while the first is saving is
//      dropped (synchronous, before any await).
//   2. openShifts() — the person's open shifts, read fresh. Any → refuse with
//      "You're already clocked in since 7:02 AM". A read failure is NOT a
//      refusal (never trap a person off the clock).
//   3. claim() — a transaction on tenants/{tid}/clockLocks/{personId} that
//      points the lock at the NEW entry id. A lock that still points at an
//      open shift, or at a clock-in claimed under 2 minutes ago whose entry
//      does not exist yet, is HELD → refuse. firestore.rules enforces the same
//      thing on the clockEntries create (the backstop for two devices at once
//      and for raw SDK writes). If the lock cannot be read at all (rules for
//      clockLocks not deployed yet), the clock-in proceeds without it.
// The caller then writes the entry WITH THAT ID, and calls release() if the
// write fails, so a retry is not blocked for 2 minutes.
//
// Clock-out: pickShiftToClose() — a person with more than one open shift is
// never silently closed out of the newest; both are shown (start + job) and
// the person (or Workforce) picks.
//
// stuck-shift-morning: an open shift from an EARLIER day (or > 14 h) does not
// lock the person out — guardClockIn({ resolveStale:true }) asks for the time
// they finished, closes it (closedLate) and lets the clock-in proceed. See
// the section above `api` at the bottom.
(function (root) {
  'use strict';

  const CLAIM_TTL_MS = 2 * 60 * 1000;   // mirrors firestore.rules clockLockHeld
  const ID_RE = /^[A-Za-z0-9_-]{1,128}$/; // mirrors firestore.rules clockLocks entryId

  const _inFlight = new Set();

  function begin(personId) {
    const k = String(personId || '');
    if (!k) return () => {};
    if (_inFlight.has(k)) return null;
    _inFlight.add(k);
    let done = false;
    return () => { if (!done) { done = true; _inFlight.delete(k); } };
  }

  function toMs(v) {
    if (v == null || v === '') return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }

  // Open = not completed and no clock-out stamped. A legacy entry with no
  // status but no clockOut counts as open (it is still running).
  function isOpen(e) {
    if (!e) return false;
    if (e.clockOut != null && e.clockOut !== '') return false;
    const s = e.status;
    return s == null || s === '' || s === 'active';
  }

  function sortNewestFirst(list) {
    return (list || []).slice().sort((a, b) => toMs(b && b.clockIn) - toMs(a && a.clockIn));
  }

  // clock-dst-all — the zone math lives in window.TenantLocalTime (one helper
  // for every typed clock time). Read at CALL time (Pitfall #1); in Node the
  // unit tests get it by require.
  function _tlt() {
    if (root && root.TenantLocalTime) return root.TenantLocalTime;
    try { if (typeof require === 'function') return require('./tenantLocalTime.js'); } catch (_) { /* browser without the helper */ }
    return null;
  }

  // The company zone for LABELS. When it cannot be read (unknown name / not
  // loaded) labels fall back to Chicago — but readStaleEnd refuses (-3), so a
  // label is never the basis of a saved time.
  function _tz(opts) {
    if (opts && opts.tz) return opts.tz;
    const T = _tlt();
    const z = T ? T.resolveZone({ allowDefault: true }) : null;
    return (z && z.ok && z.tz) || 'America/Chicago';
  }

  // The zone a typed time is READ in: null = refuse (never the phone's zone).
  function _readTz(opts) {
    const T = _tlt();
    if (!T) return (opts && opts.tz) || null;
    const z = T.resolveZone(opts && opts.tz ? { tz: opts.tz } : undefined);
    return z.ok ? z.tz : null;
  }

  function _dayKey(ms, tz) {
    try { return new Date(ms).toLocaleDateString('en-CA', { timeZone: tz }); } catch (_) { return new Date(ms).toDateString(); }
  }

  // "7:02 AM" when the shift started today (tenant zone), else "Thu, Oct 1, 7:02 AM".
  function startLabel(entry, opts) {
    const ms = toMs(entry && entry.clockIn);
    if (!ms) return 'an unknown time';
    const tz = _tz(opts);
    const now = (opts && opts.nowMs) || Date.now();
    const sameDay = _dayKey(ms, tz) === _dayKey(now, tz);
    const o = sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try { return new Date(ms).toLocaleString('en-US', { ...o, timeZone: tz }); } catch (_) { return new Date(ms).toLocaleString('en-US', o); }
  }

  function whereLabel(entry) {
    if (!entry) return '';
    const c = entry.customerName || '';
    const j = entry.jobTitle || '';
    if (c && j) return c + ' — ' + j;
    return c || j || 'no job';
  }

  function _first(name) { return String(name || '').trim().split(/\s+/)[0] || ''; }

  // The refusal, in plain English, for the screen the tap came from.
  function alreadyInMessage(entry, opts) {
    const self = !opts || opts.self !== false;
    const where = whereLabel(entry);
    const since = startLabel(entry, opts);
    const name = (opts && opts.personName) || 'This person';
    if (self) return `You're already clocked in since ${since} (${where}). Clock out of that shift first.`;
    return `${name} is already clocked in since ${since} (${where}). Clock ${_first(name) ? 'them' : 'that shift'} out first.`;
  }

  function pendingMessage(opts) {
    const self = !opts || opts.self !== false;
    const name = (opts && opts.personName) || 'this person';
    return self
      ? "Your clock-in is already being saved on another screen. Wait a few seconds and check before tapping again."
      : `A clock-in for ${name} is already being saved on another screen. Wait a few seconds and check before trying again.`;
  }

  function _isDenied(e) {
    const c = e && (e.code || '');
    return c === 'permission-denied' || c === 'firestore/permission-denied';
  }

  // TASK-9713 — the SELF clock-in write. firestore.rules (clockInNearNow) only
  // takes a self clockIn within 15 min of the server's clock, or within 24 h when
  // the entry says needsReview:true. Two honest cases fall outside 15 min:
  //   - a clock-in tapped OFFLINE: the persisted queue sends it whenever signal
  //     returns, maybe from a page that is long gone, so it is marked up front;
  //   - a phone whose clock is far off (TASK-9190 saw one ~59 min fast): refused
  //     once, then sent again marked.
  // Either way the shift is kept at the time tapped and lands in Payroll Prep's
  // Needs review (a driver can never clear that mark). Any other refusal (the
  // open-shift lock) is refused again and thrown as before.
  async function writeClockIn(setDocFn, ref, data) {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const d = (data && data.needsReview !== true && nav && nav.onLine === false) ? { ...data, needsReview: true } : data;
    try {
      return await setDocFn(ref, d);
    } catch (e) {
      if (!_isDenied(e) || !d || d.needsReview === true) throw e;
      return setDocFn(ref, { ...d, needsReview: true });
    }
  }

  // A slow yard network must not hold a clock-in or clock-out hostage to this
  // check: past `ms` the caller proceeds as if the check could not run.
  const READ_TIMEOUT_MS = 5000;
  function _withTimeout(p, ms) {
    let t;
    return Promise.race([
      p.finally(() => clearTimeout(t)),
      new Promise((_, rej) => { t = setTimeout(() => { const e = new Error('timed out'); e.code = 'timeout'; rej(e); }, ms); }),
    ]);
  }

  // The person's open shifts, newest first. null = could not read (caller
  // proceeds; the rules backstop still applies).
  async function openShifts(deps, tenantId, personId) {
    if (!deps || !tenantId || !personId) return null;
    try {
      const { db, collection, query, where, getDocs } = deps;
      const snap = await _withTimeout(getDocs(query(
        collection(db, 'tenants', tenantId, 'clockEntries'),
        where('personId', '==', personId),
        where('status', '==', 'active')
      )), READ_TIMEOUT_MS);
      const list = [];
      snap.forEach((d) => { const e = { id: d.id, ...d.data() }; if (isOpen(e)) list.push(e); });
      return sortNewestFirst(list);
    } catch (e) {
      console.warn('[ClockShiftGuard] open-shift read failed (not refusing):', e && e.message);
      return null;
    }
  }

  function _lockRef(deps, tenantId, personId) {
    return deps.doc(deps.db, 'tenants', tenantId, 'clockLocks', personId);
  }

  // Point the person's lock at newEntryId.
  //   { ok:true,  locked:true }                      — claimed
  //   { ok:true,  locked:false, reason }             — lock unavailable; proceed without it
  //   { ok:false, held:{ entryId, pending } }        — someone else's clock-in holds it
  async function claim(deps, tenantId, personId, newEntryId, closedIds) {
    if (!deps || !deps.runTransaction || !deps.getDoc || !tenantId || !personId || !newEntryId) return { ok: true, locked: false, reason: 'no-deps' };
    if (!ID_RE.test(String(personId)) || !ID_RE.test(String(newEntryId))) return { ok: true, locked: false, reason: 'unsafe-id' };
    const lockRef = _lockRef(deps, tenantId, personId);
    const by = (deps.auth && deps.auth.currentUser && deps.auth.currentUser.uid) || null;
    try {
      // The entry the lock points at is checked OUTSIDE the transaction: a
      // self-only session may read its own entries but gets permission-denied
      // reading one that does not exist yet (a pending claim), and a denied read
      // would sink the transaction. Inside it, only the lock is read — if it
      // moved since the check, the check is redone (optimistic, max 3 rounds).
      const deadline = Date.now() + 2 * READ_TIMEOUT_MS;
      for (let round = 0; round < 3; round++) {
        const left = Math.max(500, deadline - Date.now());
        const pre = await _withTimeout(deps.getDoc(lockRef), left);
        const preId = pre.exists() ? ((pre.data() || {}).entryId || null) : null;
        if (preId && preId !== newEntryId && ID_RE.test(String(preId)) && !(closedIds && closedIds.includes(preId))) {
          let es = null;
          try { es = await _withTimeout(deps.getDoc(deps.doc(deps.db, 'tenants', tenantId, 'clockEntries', preId)), left); } catch (_) { es = null; }
          if (es && es.exists()) {
            if (isOpen(es.data())) return { ok: false, held: { entryId: preId, pending: false } };
          } else {
            const at = toMs((pre.data() || {}).claimedAt);
            if (at && Date.now() - at < CLAIM_TTL_MS) return { ok: false, held: { entryId: preId, pending: true } };
          }
        }
        let moved = false;
        // A transaction that times out here may still commit later — harmless:
        // it points the lock at the id this clock-in writes anyway.
        await _withTimeout(deps.runTransaction(deps.db, async (tx) => {
          moved = false;
          const ls = await tx.get(lockRef);
          const nowId = ls.exists() ? ((ls.data() || {}).entryId || null) : null;
          if (nowId !== preId) { moved = true; return; }
          tx.set(lockRef, { personId, entryId: newEntryId, claimedAt: deps.serverTimestamp(), by });
        }), Math.max(500, deadline - Date.now()));
        if (!moved) return { ok: true, locked: true };
      }
      return { ok: false, held: { entryId: null, pending: true } };
    } catch (e) {
      // Rules for clockLocks not live (default deny), offline, or a person id
      // the rules refuse: never trap the clock-in — the clockEntries rule still
      // decides on the create.
      console.warn('[ClockShiftGuard] lock unavailable, clocking in without it:', e && (e.code || e.message));
      return { ok: true, locked: false, reason: _isDenied(e) ? 'denied' : 'error' };
    }
  }

  // Undo a claim whose entry was never written (best effort, only our own).
  async function release(deps, tenantId, personId, entryId) {
    if (!deps || !deps.runTransaction || !tenantId || !personId || !entryId || !ID_RE.test(String(personId))) return;
    try {
      const lockRef = _lockRef(deps, tenantId, personId);
      await deps.runTransaction(deps.db, async (tx) => {
        const ls = await tx.get(lockRef);
        if (ls.exists() && (ls.data() || {}).entryId === entryId) tx.delete(lockRef);
      });
    } catch (e) {
      console.warn('[ClockShiftGuard] release failed (lock expires in 2 min):', e && (e.code || e.message));
    }
  }

  // The whole clock-in guard. Returns { ok:true, entryId, release(), locked }
  // or { ok:false, message, openEntry? }. Caller shows `message` and stops.
  //
  // stuck-shift-morning — o.resolveStale: when EVERY open shift is stale (an
  // earlier local day, or open > 14 h — see isStaleOpen) the person is not
  // simply refused: promptStaleClose asks for the time they finished, the
  // shift is closed with it (closeStaleShift — the ordinary self clock-out
  // update, flagged closedLate), and the guard runs again so the clock-in
  // proceeds. A same-day open shift (the actual double tap) is refused as
  // before. Cancel / a failed save → { ok:false, stale:true, message }.
  async function guardClockIn(deps, o) {
    const { tenantId, personId } = o || {};
    const msgOpts = { self: o && o.self !== false, personName: o && o.personName, tz: o && o.tz };
    // stuck-shift-morning — shifts this guard just closed: a read right after a
    // transaction can still serve the pre-close copy from the local cache.
    const closedIds = (o && o._closedIds) || [];
    const open0 = await openShifts(deps, tenantId, personId);
    const open = open0 && open0.filter((e) => !closedIds.includes(e.id));
    if (open && open.length) {
      const nowMs = Date.now();
      const fresh = open.filter((e) => !isStaleOpen(e, { nowMs, tz: msgOpts.tz }));
      if (fresh.length || !(o && o.resolveStale) || ((o && o._staleRound) || 0) >= 3) {
        const e = fresh[0] || open[0];
        return { ok: false, message: alreadyInMessage(e, msgOpts), openEntry: e };
      }
      // Oldest stale shift first; its finish may not run past the next one's start.
      const oldestFirst = open.slice().reverse();
      const stale = oldestFirst[0];
      const next = oldestFirst[1] || null;
      const r = await promptStaleClose(deps, {
        tenantId, entry: stale, nextStartMs: next ? toMs(next.clockIn) : null,
        self: msgOpts.self, personName: msgOpts.personName, tz: msgOpts.tz, surface: o && o.surface,
      });
      if (!r || !r.closed) {
        return { ok: false, stale: true, cancelled: !!(r && r.cancelled), message: staleStillOpenMessage(stale, msgOpts), openEntry: stale };
      }
      return guardClockIn(deps, { ...o, _staleRound: ((o && o._staleRound) || 0) + 1, _closedIds: closedIds.concat(stale.id) });
    }
    const entryId = (o && o.entryId) || null;
    const c = await claim(deps, tenantId, personId, entryId, closedIds);
    if (!c.ok) {
      if (!c.held.pending) {
        const again = await openShifts(deps, tenantId, personId);
        const e = (again && again.filter((x) => !closedIds.includes(x.id))[0]) || null;
        if (e) return { ok: false, message: alreadyInMessage(e, msgOpts), openEntry: e };
      }
      return { ok: false, message: pendingMessage(msgOpts) };
    }
    return {
      ok: true, entryId, locked: !!c.locked,
      release: () => (c.locked ? release(deps, tenantId, personId, entryId) : Promise.resolve()),
    };
  }

  // A clockEntries create the rules refused because the person is already in
  // (the two-devices-at-once backstop): permission-denied right after a claim
  // could not be taken.
  function isDuplicateRefusal(err) { return _isDenied(err); }

  function _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function _elapsed(entry, nowMs) {
    const ms = toMs(entry && entry.clockIn);
    if (!ms) return '';
    const mins = Math.max(0, Math.round(((nowMs || Date.now()) - ms) / 60000));
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }

  // More than one open shift: show them all and let the person choose which
  // one to close. Resolves the chosen entry, or null on Cancel. One shift (or
  // none) resolves straight away with no dialog.
  function pickShiftToClose(entries, opts) {
    const list = sortNewestFirst((entries || []).filter(isOpen));
    if (list.length <= 1) return Promise.resolve(list[0] || null);
    const doc = root && root.document;
    if (!doc) return Promise.resolve(null);
    const self = !opts || opts.self !== false;
    const name = (opts && opts.personName) || '';
    const prev = doc.getElementById('clock-shift-chooser');
    if (prev) prev.remove();
    return new Promise((resolve) => {
      const nowMs = Date.now();
      const rows = list.map((e, i) => `
          <button type="button" class="btn btn-secondary" data-shift-choice="${_esc(e.id)}" data-idx="${i}"
            style="display:block;width:100%;text-align:left;margin:0 0 10px 0;padding:12px 14px;min-height:56px;border-radius:10px;white-space:normal;line-height:1.35;color:var(--text-primary,#0F172A);">
            <span style="display:block;font-weight:700;">Started ${_esc(startLabel(e, opts))}${i === 0 ? ' <span style="font-weight:600;color:var(--text-secondary,#475569);">(newest)</span>' : ''}</span>
            <span style="display:block;font-size:0.85rem;color:var(--text-secondary,#475569);">${_esc(whereLabel(e))} · open ${_esc(_elapsed(e, nowMs))}</span>
            <span style="display:block;font-size:0.8rem;font-weight:600;color:#B91C1C;margin-top:4px;">Close this shift</span>
          </button>`).join('');
      const modal = doc.createElement('div');
      modal.className = 'modal';
      modal.id = 'clock-shift-chooser';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.style.zIndex = '10050';
      modal.innerHTML = `
        <div class="modal-content modal-small" style="max-width:440px;width:calc(100% - 32px);">
          <div class="modal-body" style="padding:20px 16px 16px;text-align:center;">
            <span class="material-icons" style="font-size:40px;color:#B45309;">warning</span>
            <h3 style="margin:6px 0 6px;font-size:1.1rem;color:var(--text-primary,#0F172A);">${self ? 'Which shift are you clocking out of?' : `Which of ${_esc(name || 'their')}${name ? "'s" : ''} shifts are you closing?`}</h3>
            <p style="margin:0 0 14px;font-size:0.88rem;line-height:1.4;color:var(--text-secondary,#475569);">${self ? 'You have' : (_esc(name || 'This person') + ' has')} ${list.length} open shifts. Only one is real. Pick the shift to close; the other stays open.</p>
            <div style="text-align:left;">${rows}</div>
            <button type="button" class="btn btn-secondary" data-shift-choice-cancel="1" style="width:100%;min-height:44px;margin-top:2px;">Cancel</button>
          </div>
        </div>`;
      let settled = false;
      const done = (val) => { if (settled) return; settled = true; modal.remove(); resolve(val); };
      modal.addEventListener('click', (ev) => {
        if (ev.target === modal) return done(null);
        const btn = ev.target && ev.target.closest ? ev.target.closest('button') : null;
        if (!btn) return;
        if (btn.hasAttribute('data-shift-choice-cancel')) return done(null);
        const idx = btn.getAttribute('data-idx');
        if (idx != null) return done(list[Number(idx)] || null);
      });
      doc.body.appendChild(modal);
    });
  }

  // ============================================
  // stuck-shift-morning — a shift left open on an EARLIER day
  // ============================================
  // A person who forgot to clock out yesterday must not be locked out this
  // morning, and the forgotten hours must never be dropped or guessed. The
  // clock-in offers to close the old shift with the time the person enters
  // (bounded: after the start, not in the future, not more than 24 h after the
  // start, not past the next open shift's start). The close is the ordinary
  // self clock-out UPDATE on clockEntries (same fields as dispatch.js
  // submitDriverClockOut / the board's STEP 1 — the existing rules arms allow
  // it unchanged: clockOut >= clockIn and <= server now + 10 min), plus
  // closedLate / lateCloseBy / needsReview so Workforce → Hours, Reports and
  // the payroll CSV flag it (needsReview is the existing TASK-1180 marker every
  // flag surface already reads). The linked ticket line is NOT touched — the
  // office finishes that ticket (the flag says so).
  const STALE_HOURS = 14;          // open longer than this = forgotten, whatever the day
  // An earlier-day shift open less than this is a night shift still being
  // worked (COD 2026-10-02: 7 PM–5 AM drivers re-tapping at 1–3 AM), not forgotten.
  const STALE_MIN_HOURS_PRIOR_DAY = 10;
  const MAX_LATE_CLOSE_HOURS = 24;

  function isStaleOpen(entry, opts) {
    const ms = toMs(entry && entry.clockIn);
    if (!ms) return false; // unreadable start: no bounds to offer — keep the plain refusal
    const nowMs = (opts && opts.nowMs) || Date.now();
    const openMs = nowMs - ms;
    if (openMs > STALE_HOURS * 3600000) return true;
    const tz = _tz(opts);
    return _dayKey(ms, tz) < _dayKey(nowMs, tz) && openMs >= STALE_MIN_HOURS_PRIOR_DAY * 3600000;
  }

  function _dayLabel(ms, tz) {
    try { return new Date(ms).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }); } catch (_) { return new Date(ms).toDateString(); }
  }
  function _timeLabel(ms, tz) {
    try { return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }); } catch (_) { return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  }

  // "You never clocked out on Thu, Oct 1 (started 6:40 AM, Acme — Job X)."
  function staleIntro(entry, opts) {
    const tz = _tz(opts);
    const ms = toMs(entry && entry.clockIn);
    const self = !opts || opts.self !== false;
    const who = self ? 'You' : ((opts && opts.personName) || 'This person');
    return `${who} never clocked out on ${_dayLabel(ms, tz)} (started ${_timeLabel(ms, tz)}, ${whereLabel(entry)}).`;
  }

  function staleStillOpenMessage(entry, opts) {
    const self = !opts || opts.self !== false;
    return `${staleIntro(entry, opts)} Not clocked in — that shift is still open. ${self ? 'Enter the time you finished to close it, or ask the office.' : 'Close it in Workforce first.'}`;
  }

  // Offset (ms) of `tz` from UTC at instant `ms`.
  function _tzOffsetMs(ms, tz) {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const p = {};
    f.formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value; });
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return asUtc - Math.floor(ms / 1000) * 1000;
  }
  // Wall-clock "YYYY-MM-DD" + HH:MM in `tz` → epoch ms (DST-safe: re-checked once).
  function zonedToMs(ymd, hh, mm, tz) {
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d) return NaN;
    const guess = Date.UTC(y, m - 1, d, hh, mm);
    try {
      let ms = guess - _tzOffsetMs(guess, tz);
      ms = guess - _tzOffsetMs(ms, tz);
      return ms;
    } catch (_) {
      return new Date(y, m - 1, d, hh, mm).getTime();
    }
  }

  // True when `ms` reads back as the same wall-clock day + HH:MM in `tz`.
  function zonedWallMatches(ms, ymd, hh, mm, tz) {
    if (!Number.isFinite(ms)) return false;
    try {
      const f = new Intl.DateTimeFormat('en-CA', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      const p = {};
      f.formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value; });
      return `${p.year}-${p.month}-${p.day}` === ymd && ((+p.hour) % 24) === hh && (+p.minute) === mm;
    } catch (_) { return true; }
  }

  // Every instant whose wall clock in `tz` reads ymd HH:MM, earliest first:
  // one on a normal day, NONE in a spring-forward gap, TWO in the repeated
  // fall-back hour (America/Chicago 2026-11-01 1:30 AM is 06:30Z CDT and
  // 07:30Z CST). zonedToMs returns only one of the two, so a finish time in the
  // repeated hour must never be read through it alone (COD v1.15.2008).
  function zonedCandidates(ymd, hh, mm, tz) {
    // Unknown zone / no Intl → null = refuse (readStaleEnd -3) — never fall
    // back to the device zone and silently pick one (COD v1.15.2008 follow-up).
    if (!tz) return null;
    const T = _tlt();
    if (T) return T.candidates(ymd, hh, mm, tz);
    return null;
  }

  // "1:30 AM CDT" — the zone abbreviation tells the two repeated times apart.
  function _timeZoneLabel(ms, tz) {
    try { return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }); } catch (_) { return _timeLabel(ms, tz); }
  }

  // The two choices in the repeated hour, or [] when the time is not ambiguous.
  function ambiguousEndChoices(ymd, hh, mm, tz) {
    const c = zonedCandidates(ymd, hh, mm, tz);
    if (!c || c.length < 2) return [];
    return [
      { which: 'first', ms: c[0], label: _timeZoneLabel(c[0], tz) + ' (first)' },
      { which: 'second', ms: c[c.length - 1], label: _timeZoneLabel(c[c.length - 1], tz) + ' (second)' },
    ];
  }

  // Wall time the person typed → the instant to save. -1 = that time does not
  // exist (spring-forward gap); -2 = it happened twice (fall-back) and the
  // person has not said which one — NEVER silently picked; -3 = the company
  // time zone could not be read; NaN = unreadable.
  function readStaleEnd(ymd, hh, mm, tz, which) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd)) || !Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
    const c = zonedCandidates(ymd, hh, mm, tz);
    if (!c) return -3;
    if (!c.length) return -1;
    if (c.length === 1) return c[0];
    if (which === 'first') return c[0];
    if (which === 'second') return c[c.length - 1];
    return -2;
  }

  // The days a finish time may fall on: the start day, and the next day while
  // it is not in the future (a shift past midnight).
  function staleEndDays(entry, opts) {
    const tz = _tz(opts);
    const nowMs = (opts && opts.nowMs) || Date.now();
    const startMs = toMs(entry && entry.clockIn);
    const startKey = _dayKey(startMs, tz);
    const days = [{ ymd: startKey, label: _dayLabel(startMs, tz) + ' (the day it started)' }];
    const nextNoon = zonedToMs(startKey, 12, 0, tz) + 24 * 3600000;
    const nextKey = _dayKey(nextNoon, tz);
    if (nextKey !== startKey && nextKey <= _dayKey(nowMs, tz)) days.push({ ymd: nextKey, label: _dayLabel(nextNoon, tz) + ' (next day)' });
    return days;
  }

  // Pure bounds check. { ok:true, endMs, hours } or { ok:false, error }.
  function validateStaleEnd(entry, endMs, opts) {
    const tz = _tz(opts);
    const nowMs = (opts && opts.nowMs) || Date.now();
    const startMs = toMs(entry && entry.clockIn);
    if (endMs === -1) return { ok: false, error: "That time doesn't exist that day (the clocks changed). Pick a time just after it." };
    if (endMs === -2) return { ok: false, error: 'The clocks fell back that night, so that time happened twice. Pick which one.' };
    if (endMs === -3) return { ok: false, error: "Couldn't read the company time zone. Ask the office to close this shift in Workforce." };
    if (!Number.isFinite(endMs) || !startMs) return { ok: false, error: 'Pick the day and the time you finished.' };
    if (endMs <= startMs) return { ok: false, error: `The finish time must be after the shift started (${_timeLabel(startMs, tz)}).` };
    if (endMs > nowMs) return { ok: false, error: "The finish time can't be in the future — check AM vs PM." };
    if (endMs - startMs > MAX_LATE_CLOSE_HOURS * 3600000) return { ok: false, error: `That is more than 24 hours after the shift started. Ask the office to fix this shift in Workforce.` };
    const next = opts && opts.nextStartMs;
    if (next && endMs > next) return { ok: false, error: `The finish time must be before your next shift started (${_dayLabel(next, tz)} ${_timeLabel(next, tz)}).` };
    return { ok: true, endMs, hours: Math.round(((endMs - startMs) / 3600000) * 100) / 100 };
  }

  function _hm(hours) {
    const mins = Math.round(hours * 60);
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }

  // The close itself: the ordinary self clock-out update + the late-close flags.
  // Throws on failure (the dialog shows it and stays open).
  async function closeStaleShift(deps, o) {
    const { tenantId, entry, endMs, hours } = o;
    if (!deps || !deps.updateDoc || !deps.Timestamp || !tenantId || !entry || !entry.id) throw new Error('close unavailable');
    const st = root && root.AppState;
    const self = o.self !== false;
    // A display name only — never an email: the worker can read their own entry.
    const actorName = (st && st.driverModePin && st.driverPersonName)
      || (self ? (o.personName || entry.personName || null) : null)
      || (st && st.user && st.user.displayName)
      || (self ? null : 'the office');
    const lateCloseBy = {
      uid: (deps.auth && deps.auth.currentUser && deps.auth.currentUser.uid) || (st && st.user && st.user.uid) || null,
      name: actorName || null,
      self,
      surface: o.surface || null,
    };
    const ref = deps.doc(deps.db, 'tenants', tenantId, 'clockEntries', entry.id);
    const update = {
      clockOut: deps.Timestamp.fromMillis(endMs),
      status: 'completed',
      totalHours: hours,
      hours,
      closedLate: true,
      lateCloseBy,
      lateClosedAt: deps.serverTimestamp ? deps.serverTimestamp() : deps.Timestamp.now(),
      needsReview: true,
      driverAdjustReason: `Never clocked out. Finish time ${_dayLabel(endMs, _tz(o))} ${_timeLabel(endMs, _tz(o))} entered at the next clock-in${actorName ? ' by ' + actorName : ''}.`,
      updatedAt: deps.Timestamp.now(),
    };
    let write = null;
    try {
      // COD 2026-10-02: never overwrite a close-out (or an office correction)
      // that landed while the dialog was open — re-read the entry inside a
      // transaction; still open with the same start, or nothing is written.
      const startMs = toMs(entry.clockIn);
      write = deps.runTransaction
        ? deps.runTransaction(deps.db, async (tx) => {
            const s = await tx.get(ref);
            if (!s.exists()) { const er = new Error('entry gone'); er.code = 'stale-gone'; throw er; }
            const cur = s.data() || {};
            if (!isOpen(cur)) { const er = new Error('already closed'); er.code = 'stale-closed'; throw er; }
            if (toMs(cur.clockIn) !== startMs) { const er = new Error('start changed'); er.code = 'stale-changed'; throw er; }
            tx.update(ref, update);
          })
        : deps.updateDoc(ref, update);
      await _withTimeout(Promise.resolve(write), 15000);
    } catch (e) {
      // A slow yard network: the write may still have landed. Read it back
      // before telling the person it failed.
      if (!e || e.code !== 'timeout') throw e;
      // clock-dst-all (4d flake): the close is still IN FLIGHT — keep waiting
      // for it instead of reporting a failure that later turns out false (the
      // shift closed, but the clock-in that should follow was abandoned and the
      // person saw "couldn't save"). Its own error (already closed / changed)
      // still reaches the dialog.
      let settled = false;
      try { await _withTimeout(Promise.resolve(write), 30000); settled = true; } catch (e2) {
        if (e2 && e2.code !== 'timeout') throw e2;
      }
      if (!settled) {
        if (!deps.getDoc) throw e;
        let s = null;
        try { s = await _withTimeout(Promise.resolve(deps.getDoc(ref)), 5000); } catch (_) { throw e; }
        if (!s || !s.exists() || isOpen(s.data())) throw e;
      }
    }
    // Audit trail, best effort (never blocks the clock-in that follows).
    try {
      const after = { ...entry, clockOut: update.clockOut, status: 'completed', totalHours: hours, hours };
      if (root && root.ClockModule && typeof root.ClockModule.logClockEntryChange === 'function') {
        root.ClockModule.logClockEntryChange(tenantId, entry.id, entry, after, 'edit', 'late-close-at-clock-in');
      }
      if (deps.addDoc && deps.collection) {
        // Same actor shape as clock.js logClockAudit — firestore.rules
        // (auditActorIsSelf / auditDisplayActorIsSelf) pin `user` to the
        // caller's email and allow `actorName` only for a PIN driver's own name.
        const email = (st && st.user && st.user.email) || '';
        deps.addDoc(deps.collection(deps.db, 'tenants', tenantId, 'clockAuditLog'), {
          action: 'CLOCK_OUT', source: 'late-close-at-clock-in', surface: o.surface || null,
          timestamp: deps.Timestamp.now(), user: email || 'unknown', actorName: email ? null : ((st && st.driverPersonName) || null),
          clockEntryId: entry.id, personId: entry.personId || null, personName: entry.personName || null,
          jobId: entry.jobId || null, ticketId: entry.ticketId || null, ticketLineId: entry.ticketLineId || null,
          totalHours: hours, clockOut: update.clockOut, closedLate: true,
        }).catch((e) => console.warn('[ClockShiftGuard] late-close audit row failed:', e && (e.code || e.message)));
      }
    } catch (e) {
      console.warn('[ClockShiftGuard] late-close audit failed:', e && e.message);
    }
    return update;
  }

  // The dialog. Resolves { closed:true, endMs, hours } after a successful
  // close, or { cancelled:true }. Nothing is written until the person picks a
  // time and taps the button — the time box starts EMPTY (never auto-picked).
  function promptStaleClose(deps, o) {
    const doc = root && root.document;
    const entry = o && o.entry;
    if (!doc || !entry || !deps || !deps.updateDoc || !deps.Timestamp) return Promise.resolve({ cancelled: true });
    const tz = _tz(o);
    // clock-dst-all — the zone the typed finish time is READ in. null (unknown
    // zone / settings not loaded) → readStaleEnd -3 → refused with a message.
    const readTz = _readTz(o);
    const self = o.self !== false;
    const days = staleEndDays(entry, o);
    const prev = doc.getElementById('clock-stale-shift');
    if (prev) prev.remove();
    return new Promise((resolve) => {
      const modal = doc.createElement('div');
      modal.className = 'modal';
      modal.id = 'clock-stale-shift';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'clock-stale-shift-title');
      modal.style.zIndex = '10050';
      const dayOpts = days.map((d) => `<option value="${_esc(d.ymd)}">${_esc(d.label)}</option>`).join('');
      const fieldStyle = 'width:100%;min-height:44px;padding:8px 10px;font-size:1rem;border:1px solid var(--border-light,#CBD5E1);border-radius:8px;background:#fff;color:var(--text-primary,#0F172A);';
      modal.innerHTML = `
        <div class="modal-content modal-small" style="max-width:440px;width:calc(100% - 32px);">
          <div class="modal-body" style="padding:20px 16px 16px;">
            <div style="text-align:center;">
              <span class="material-icons" style="font-size:40px;color:#B45309;">history_toggle_off</span>
              <h3 id="clock-stale-shift-title" style="margin:6px 0 6px;font-size:1.1rem;color:var(--text-primary,#0F172A);">${self ? 'You never clocked out' : _esc(o.personName || 'This person') + ' never clocked out'}</h3>
            </div>
            <p style="margin:0 0 12px;font-size:0.9rem;line-height:1.4;color:var(--text-primary,#0F172A);">${_esc(staleIntro(entry, o))} Enter the time ${self ? 'you' : 'they'} finished to close it, then ${self ? "you'll be" : "they'll be"} clocked in.</p>
            <label for="stale-shift-end-day" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-secondary,#475569);margin-bottom:4px;">Finished on</label>
            <select id="stale-shift-end-day" style="${fieldStyle}margin-bottom:10px;">${dayOpts}</select>
            <label for="stale-shift-end-time" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-secondary,#475569);margin-bottom:4px;">Finish time</label>
            <input type="time" id="stale-shift-end-time" required style="${fieldStyle}">
            <div data-stale-dst role="radiogroup" aria-label="Which time" style="display:none;margin:8px 0 0;"></div>
            <div data-stale-preview style="min-height:1.2em;margin:8px 0 0;font-size:0.85rem;color:var(--text-secondary,#475569);"></div>
            <div data-stale-error role="alert" style="min-height:1.2em;margin:4px 0 0;font-size:0.85rem;font-weight:600;color:#B91C1C;"></div>
            <p style="margin:8px 0 14px;font-size:0.78rem;line-height:1.35;color:var(--text-secondary,#475569);">The office will see this shift was closed late. Still working that shift? Tap Cancel.</p>
            <button type="button" class="btn btn-primary" data-stale-close-submit="1" style="width:100%;min-height:48px;font-weight:700;margin-bottom:8px;">Close that shift${self ? ' &amp; clock in' : ''}</button>
            <button type="button" class="btn btn-secondary" data-stale-close-cancel="1" style="width:100%;min-height:44px;">Cancel</button>
          </div>
        </div>`;
      const $ = (sel) => modal.querySelector(sel);
      const daySel = $('#stale-shift-end-day');
      const timeIn = $('#stale-shift-end-time');
      const errEl = $('[data-stale-error]');
      const prevEl = $('[data-stale-preview]');
      const submit = $('[data-stale-close-submit]');
      const dstEl = $('[data-stale-dst]');
      const vopts = { tz, nextStartMs: o.nextStartMs };
      // Fall-back night: the person picks which of the two times they meant.
      // The pick is tied to the day+time it was made for; changing either clears it.
      let dstKey = '';
      let dstPick = null;
      const parseTime = () => /^(\d{1,2}):(\d{2})/.exec(String(timeIn.value || ''));
      const readEnd = () => {
        const mm = parseTime();
        if (!mm) return NaN;
        const key = daySel.value + ' ' + mm[1] + ':' + mm[2];
        // Spring-forward gap → -1 (refused); repeated hour with no pick → -2 (refused).
        return readStaleEnd(daySel.value, Number(mm[1]), Number(mm[2]), readTz, dstKey === key ? dstPick : null);
      };
      const renderDst = () => {
        const mm = parseTime();
        const choices = mm && readTz ? ambiguousEndChoices(daySel.value, Number(mm[1]), Number(mm[2]), readTz) : [];
        if (!choices.length) { dstEl.style.display = 'none'; dstEl.innerHTML = ''; dstKey = ''; dstPick = null; return; }
        const key = daySel.value + ' ' + mm[1] + ':' + mm[2];
        if (dstKey !== key) { dstKey = key; dstPick = null; }
        const now = Date.now();
        dstEl.innerHTML = `<div style="font-size:0.85rem;font-weight:600;color:var(--text-primary,#0F172A);margin-bottom:6px;">The clocks fell back that night, so that time happened twice. Which one?</div>` + choices.map((c) => {
          const v = validateStaleEnd(entry, c.ms, { ...vopts, nowMs: now });
          const hrs = v.ok ? ` — ${_hm(v.hours)}` : '';
          return `<label style="display:flex;align-items:center;gap:8px;min-height:44px;padding:4px 8px;border:1px solid var(--border-light,#CBD5E1);border-radius:8px;margin-bottom:6px;cursor:pointer;color:var(--text-primary,#0F172A);"><input type="radio" name="stale-shift-dst" value="${_esc(c.which)}"${dstPick === c.which ? ' checked' : ''}> <span>${_esc(c.label)}${_esc(hrs)}</span></label>`;
        }).join('');
        dstEl.style.display = 'block';
      };
      const preview = () => {
        errEl.textContent = '';
        renderDst();
        const end = readEnd();
        if (end === -2) { prevEl.textContent = 'Pick which time above to see the hours.'; return; }
        if (!Number.isFinite(end) || end < 0) { prevEl.textContent = ''; return; }
        const v = validateStaleEnd(entry, end, { ...vopts, nowMs: Date.now() });
        prevEl.textContent = v.ok ? `That's ${_hm(v.hours)} for this shift.` : '';
      };
      dstEl.addEventListener('change', (ev) => {
        const t = ev.target;
        if (!t || t.name !== 'stale-shift-dst') return;
        dstPick = t.value === 'second' ? 'second' : 'first';
        errEl.textContent = '';
        const end = readEnd();
        const v = Number.isFinite(end) && end > 0 ? validateStaleEnd(entry, end, { ...vopts, nowMs: Date.now() }) : null;
        prevEl.textContent = v && v.ok ? `That's ${_hm(v.hours)} for this shift.` : '';
      });
      daySel.addEventListener('change', preview);
      timeIn.addEventListener('input', preview);
      timeIn.addEventListener('change', preview);
      let settled = false;
      let saving = false;
      const done = (val) => { if (settled) return; settled = true; modal.remove(); resolve(val); };
      modal.addEventListener('click', async (ev) => {
        if (ev.target === modal) { if (!saving) done({ cancelled: true }); return; }
        const btn = ev.target && ev.target.closest ? ev.target.closest('button') : null;
        if (!btn) return;
        if (btn.hasAttribute('data-stale-close-cancel')) { if (!saving) done({ cancelled: true }); return; }
        if (!btn.hasAttribute('data-stale-close-submit') || saving) return;
        const v = validateStaleEnd(entry, readEnd(), { ...vopts, nowMs: Date.now() });
        if (!v.ok) { errEl.textContent = v.error; return; }
        saving = true;
        submit.disabled = true;
        const label = submit.innerHTML;
        submit.textContent = 'Saving…';
        try {
          await closeStaleShift(deps, { tenantId: o.tenantId, entry, endMs: v.endMs, hours: v.hours, self, personName: o.personName, surface: o.surface, tz });
          try { root.showToast && root.showToast(`Shift from ${_dayLabel(toMs(entry.clockIn), tz)} closed at ${_timeLabel(v.endMs, tz)} (${_hm(v.hours)}). The office will review it.`, 'success', null, 6000); } catch (_) { /* toast is cosmetic */ }
          done({ closed: true, endMs: v.endMs, hours: v.hours });
        } catch (e) {
          console.warn('[ClockShiftGuard] late close failed:', e && (e.code || e.message));
          if (e && (e.code === 'stale-closed' || e.code === 'stale-gone')) {
            // Closed (or removed) elsewhere while this was open — nothing to do; carry on.
            try { root.showToast && root.showToast('That shift was already closed on another screen.', 'info', null, 5000); } catch (_) { /* cosmetic */ }
            done({ closed: true, already: true });
            return;
          }
          errEl.textContent = (e && e.code === 'stale-changed')
            ? 'This shift was just changed on another screen. Tap Cancel and try again.'
            : _isDenied(e)
            ? "This shift can't be closed from here. Ask the office to close it in Workforce."
            : "Couldn't save — check your signal and try again. If it keeps failing, ask the office to close the shift.";
          saving = false;
          submit.disabled = false;
          submit.innerHTML = label;
        }
      });
      doc.body.appendChild(modal);
      try { timeIn.focus(); } catch (_) { /* focus is cosmetic */ }
    });
  }

  const api = {
    CLAIM_TTL_MS, begin, toMs, isOpen, sortNewestFirst, startLabel, whereLabel,
    alreadyInMessage, pendingMessage, openShifts, claim, release, guardClockIn,
    isDuplicateRefusal, pickShiftToClose, writeClockIn,
    // stuck-shift-morning
    STALE_HOURS, isStaleOpen, staleIntro, staleStillOpenMessage, staleEndDays, validateStaleEnd,
    zonedToMs, zonedWallMatches, closeStaleShift, promptStaleClose,
    // DST fall-back late close
    zonedCandidates, ambiguousEndChoices, readStaleEnd,
  };
  if (root) root.ClockShiftGuard = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
